import React, { useCallback, useState } from 'react';
import { Box, Collapse, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { defineMessages, useIntl } from 'react-intl';
import { Locale, useLocale } from '../../i18n';

interface LanguageDisplay {
  flag: string;
}

const languageDisplay: Record<Locale, LanguageDisplay> = {
  en: { flag: '🇬🇧' },
  cs: { flag: '🇨🇿' },
};
const languageNameMessages = defineMessages({
  en: { id: 'language.en', defaultMessage: 'English' },
  cs: { id: 'language.cs', defaultMessage: 'Čeština' },
});

const menuItemStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1,
};
const chevronStyles = {
  transition: 'transform 150ms ease-in-out',
};
const expandedChevronStyles = {
  ...chevronStyles,
  transform: 'rotate(180deg)',
};

export const UserMenuLanguageSwitcher = React.memo(
  function UserMenuLanguageSwitcher() {
    const intl = useIntl();
    const { locale, changeLocale, changingLocale, localeError } = useLocale();
    const [expanded, setExpanded] = useState(false);
    const nextLocale: Locale = locale === 'en' ? 'cs' : 'en';
    const currentLanguage = languageDisplay[locale];
    const nextLanguage = languageDisplay[nextLocale];
    const currentLanguageName = intl.formatMessage(languageNameMessages[locale]);
    const nextLanguageName = intl.formatMessage(
      languageNameMessages[nextLocale]
    );

    const handleToggle = useCallback(() => {
      setExpanded((currentExpanded) => !currentExpanded);
    }, []);
    const handleSwitch = useCallback(async () => {
      await changeLocale(nextLocale);
      setExpanded(false);
    }, [changeLocale, nextLocale]);

    const currentLabel = intl.formatMessage(
      {
        id: 'language.current',
        defaultMessage: 'Language: {flag} {name}',
      },
      { flag: currentLanguage.flag, name: currentLanguageName }
    );
    const switchLabel = intl.formatMessage(
      {
        id: 'language.switch',
        defaultMessage: 'Switch to {flag} {name}',
      },
      { flag: nextLanguage.flag, name: nextLanguageName }
    );
    const errorTitle = localeError
      ? intl.formatMessage({
          id: 'language.saveError',
          defaultMessage: 'The language could not be saved. Please try again.',
        })
      : undefined;

    return (
      <>
        <MenuItem
          aria-controls="user-menu-language-options"
          aria-expanded={expanded}
          disabled={changingLocale}
          onClick={handleToggle}
          sx={menuItemStyles}
          title={errorTitle}
        >
          <Box component="span">{currentLabel}</Box>
          <ExpandMoreIcon
            fontSize="small"
            sx={expanded ? expandedChevronStyles : chevronStyles}
          />
        </MenuItem>
        <Collapse id="user-menu-language-options" in={expanded} unmountOnExit>
          <MenuItem disabled={changingLocale} onClick={handleSwitch}>
            {switchLabel}
          </MenuItem>
        </Collapse>
      </>
    );
  }
);
